<template>
  <div class="thunder-list">
    <!-- Header -->
    <div class="thunder-header">
      <div class="thunder-header__left">
        <h2 class="thunder-header__title">Thunder</h2>
        <span class="thunder-header__count" v-if="resources">{{ resources.length }}</span>
      </div>
      <div class="thunder-header__right">
        <div class="thunder-search">
          <i class="fa fa-search thunder-search__icon"></i>
          <input
            type="text"
            class="thunder-search__input"
            v-model="search"
            placeholder="Search resources…"
          />
        </div>
        <button class="thunder-btn thunder-btn--create" @click="resourceModal = true">
          <i class="fa fa-plus"></i>
          <span>{{ $t('createNew') }}</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div class="thunder-grid" v-if="!resources">
      <div class="thunder-card thunder-card--skeleton" v-for="n in 4" :key="n">
        <div class="thunder-card__body">
          <div class="skel skel--title"></div>
          <div class="skel skel--line skel--short"></div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div class="thunder-empty" v-else-if="resources.length === 0">
      <div class="thunder-empty__icon">
        <i class="fa fa-database"></i>
      </div>
      <p class="thunder-empty__text">{{ $t('noResources') }}</p>
      <button class="thunder-btn thunder-btn--create" @click="resourceModal = true">
        <i class="fa fa-plus"></i>
        <span>Create your first resource</span>
      </button>
    </div>

    <!-- Grid -->
    <div class="thunder-grid" v-else>
      <div
        class="thunder-card"
        v-for="item in filteredResources"
        :key="item.name"
        @click="goToResource(item)"
      >
        <div class="thunder-card__header">
          <div class="thunder-card__icon">
            <i class="fa fa-database"></i>
          </div>
        </div>
        <div class="thunder-card__body">
          <h3 class="thunder-card__name">{{ item.name }}</h3>
        </div>
      </div>
    </div>

    <!-- Create modal -->
    <main-dialog
      :show="resourceModal"
      title="New Resource"
      subtitle="Create a new Thunder resource"
      max-width="440px"
      @close="resourceModal = false"
    >
      <div class="thunder-form">
        <div class="thunder-field">
          <label class="thunder-label">{{ $t('name') }}</label>
          <input
            type="text"
            class="thunder-input"
            v-model="name"
            placeholder="Enter resource name…"
            @keydown.enter.prevent="createResource"
            ref="nameInput"
          />
        </div>
      </div>

      <template #footer>
        <button class="thunder-btn thunder-btn--cancel" @click="resourceModal = false">Cancel</button>
        <button
          class="thunder-btn thunder-btn--create"
          @click="createResource"
          :disabled="!name || name.trim() === ''"
        >
          <i class="fa fa-plus"></i>
          <span>Create</span>
        </button>
      </template>
    </main-dialog>
  </div>
</template>

<script>
import MainDialog from "@/components/MainDialog.vue";

export default {
  name: "ThunderList",
  components: { MainDialog },
  data() {
    return {
      resources: null,
      name: "",
      search: "",
      resourceModal: false,
    };
  },
  computed: {
    filteredResources() {
      if (!this.resources) return [];
      if (!this.search.trim()) return this.resources;
      const q = this.search.toLowerCase().trim();
      return this.resources.filter((i) =>
        i.name.toLowerCase().includes(q)
      );
    },
  },
  watch: {
    resourceModal(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.nameInput) this.$refs.nameInput.focus();
        });
      }
    },
  },
  methods: {
    createResource() {
      if (!this.name || this.name.trim() === "") return;
      this.goToResource({ name: this.name });
    },
    goToResource(item) {
      this.$router.push({ path: "/admin/thunder/" + item.name });
    },
  },
  mounted() {
    this.$http
      .get("/v3/system/thunder")
      .then(({ data }) => {
        this.resources = data || [];
      })
      .catch((err) => {
        this.$debugLog(err);
        this.$mainDialog.msgBoxOk(`${this.$t("failedToFetchResource")}.`);
      });
  },
};
</script>

<style scoped lang="scss">
.thunder-list {
  padding: 8px 0;
}

.thunder-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
}

.thunder-header__left {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.thunder-header__right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.thunder-search {
  position: relative;
  display: flex;
  align-items: center;
}

.thunder-search__icon {
  position: absolute;
  left: 12px;
  font-size: 12px;
  color: var(--text-muted);
  pointer-events: none;
}

.thunder-search__input {
  width: 200px;
  height: 38px;
  padding: 0 14px 0 34px;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder { color: var(--text-muted); }
  &:focus {
    border-color: var(--primary-start);
    box-shadow: 0 0 0 3px rgba(91, 124, 246, 0.12);
  }
}

.thunder-header__title {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.thunder-header__count {
  font-size: 13px;
  color: var(--text-muted);
  font-weight: 500;
}

// ─── Buttons ───────────────────────────────────
.thunder-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  outline: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.18s ease;

  &--create {
    padding: 9px 20px;
    background: var(--primary-start);
    color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);

    &:hover {
      opacity: 0.88;
      transform: translateY(-1px);
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }

  &--cancel {
    padding: 8px 18px;
    background: transparent;
    border: 1px solid var(--border-hover, #4e5058);
    color: var(--text-secondary);

    &:hover {
      background: var(--bg-card-secondary);
      color: var(--text-primary);
    }
  }
}

// ─── Grid ──────────────────────────────────────
.thunder-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

// ─── Card ──────────────────────────────────────
.thunder-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.18s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:hover {
    border-color: rgba(108, 128, 255, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  }
}

.thunder-card__header {
  padding: 16px 18px 0;
}

.thunder-card__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(0, 200, 150, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00c896;
  font-size: 15px;
}

.thunder-card__body {
  padding: 14px 18px 16px;
  flex: 1;
}

.thunder-card__name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.3;
  word-break: break-word;
}

// ─── Empty state ───────────────────────────────
.thunder-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.thunder-empty__icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: rgba(0, 200, 150, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: var(--text-muted);
  margin-bottom: 16px;
}

.thunder-empty__text {
  font-size: 15px;
  color: var(--text-muted);
  margin: 0 0 20px;
}

// ─── Form (modal) ──────────────────────────────
.thunder-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.thunder-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.thunder-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.thunder-input {
  width: 100%;
  height: 42px;
  padding: 0 14px;
  background: var(--form-bg, #383a40);
  border: 1px solid var(--form-border, #4e5058);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder { color: var(--text-muted); }
  &:focus {
    border-color: var(--primary-start);
    box-shadow: 0 0 0 3px rgba(91, 124, 246, 0.12);
  }
}

// ─── Skeleton ──────────────────────────────────
.thunder-card--skeleton {
  pointer-events: none;
  padding: 18px;

  .skel {
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.06);
    animation: skel-pulse 1.4s ease-in-out infinite;

    &--title {
      width: 60%;
      height: 18px;
      margin-bottom: 14px;
    }

    &--line {
      width: 80%;
      height: 12px;
      margin-bottom: 10px;
    }

    &--short {
      width: 45%;
    }
  }
}

@keyframes skel-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
}
</style>
